// Personal data on the device — the web counterpart of Persistence (Repositories.swift, PhotoStore.swift): recorded
// trips, personal waypoints with photos, and the hiker's own reports, in IndexedDB. Nothing here leaves the device.

import type { EpochMs, GeoCoordinate, PersonalWaypointKind, UserReport, UserTrip } from '../core/models';
import { reportIsExpired } from '../core/domain';
import { kvStore, type KeyValueStore } from '../storage/idb';

export function newId(): string {
  return crypto.randomUUID();
}

export class TripRepository {
  constructor(private readonly store: KeyValueStore<UserTrip> = kvStore('trips')) {}

  save(trip: UserTrip): Promise<void> {
    return this.store.set(trip.id, trip);
  }

  get(id: string): Promise<UserTrip | undefined> {
    return this.store.get(id);
  }

  async all(): Promise<UserTrip[]> {
    return (await this.store.values()).sort((a, b) => b.startedAt - a.startedAt);
  }

  delete(id: string): Promise<void> {
    return this.store.delete(id);
  }

  /** The trip still being recorded (no end date), if the app was closed during a recording. */
  async activeRecording(): Promise<UserTrip | undefined> {
    return (await this.all()).find((t) => t.endedAt === undefined);
  }
}

/** A personal waypoint (iOS PersonalWaypointRecord). */
export interface PersonalWaypoint {
  id: string;
  name: string;
  kind: PersonalWaypointKind;
  coordinate: GeoCoordinate;
  elevation?: number;
  note?: string;
  createdAt: EpochMs;
  updatedAt: EpochMs;
  /** Ids of photos in the photo store. */
  photoIDs: string[];
}

export class WaypointRepository {
  constructor(
    private readonly store: KeyValueStore<PersonalWaypoint> = kvStore('waypoints'),
    private readonly photos: KeyValueStore<Blob> = kvStore('photos'),
  ) {}

  async all(): Promise<PersonalWaypoint[]> {
    return (await this.store.values()).sort((a, b) => b.updatedAt - a.updatedAt);
  }

  get(id: string): Promise<PersonalWaypoint | undefined> {
    return this.store.get(id);
  }

  save(waypoint: PersonalWaypoint): Promise<void> {
    return this.store.set(waypoint.id, waypoint);
  }

  async delete(id: string): Promise<void> {
    const waypoint = await this.store.get(id);
    for (const photo of waypoint?.photoIDs ?? []) await this.photos.delete(photo);
    await this.store.delete(id);
  }

  async addPhoto(blob: Blob): Promise<string> {
    const id = newId();
    await this.photos.set(id, blob);
    return id;
  }

  photo(id: string): Promise<Blob | undefined> {
    return this.photos.get(id);
  }

  deletePhoto(id: string): Promise<void> {
    return this.photos.delete(id);
  }
}

export class ReportRepository {
  constructor(private readonly store: KeyValueStore<UserReport> = kvStore('reports')) {}

  async all(): Promise<UserReport[]> {
    return (await this.store.values()).sort((a, b) => b.observedAt - a.observedAt);
  }

  /** Reports still valid at `date` (expired reports never influence planning). */
  async active(date: EpochMs): Promise<UserReport[]> {
    return (await this.all()).filter((r) => !reportIsExpired(r, date));
  }

  save(report: UserReport): Promise<void> {
    return this.store.set(report.id, report);
  }

  delete(id: string): Promise<void> {
    return this.store.delete(id);
  }

  /** Deletes reports that expired before `date` (iOS purges after 7 days). */
  async purge(before: EpochMs): Promise<void> {
    for (const r of await this.all()) if (r.expiresAt < before) await this.store.delete(r.id);
  }
}
